/**
 * WEB-3739 юнит 1 — палитра ⌘K оболочки кабинета нового вида, по `Palette`
 * эталона `design/cabinet-rescin/app.jsx` (стили `.pal*` —
 * `styles/cabinet-shell.css`; на телефоне — шторкой снизу, как выпадашки).
 *
 * Строка ввода с фокусом; без запроса — «Разделы» до 5, с запросом — до 6 (обе
 * ветки меню из `shellMenu.ts`, у пунктов организации приписка «, Вся
 * организация»), затем «Действия и настройки» (до 4 / до 8, из
 * `paletteCommands.ts`; без команд группы нет; видны команды обоих уровней по
 * правилу `visibleCommands` — те же права и флаг черновика, что у меню; справа
 * в строке команды — подпись её раздела `.w`, доводка 2). ↑ ↓ двигают выбранный пункт,
 * Enter открывает его адрес (команды «Баланса» — дверью пополнения
 * `useOrgTopUp`, она сперва переключает организацию проекта), Esc и нажатие по затемнению закрывают. Закрыта —
 * в разметке её нет. Открывает и закрывает ⌘K / Ctrl+K оболочка
 * (`DashboardLayout.tsx`).
 *
 * «Спросить ИИ: «…»» — строка последней, только когда передан `renderAsk` и
 * есть запрос; Enter или щелчок показывают на месте списка то, что вернул
 * `renderAsk(q, назад, закрыть)` (у эталона — `PalAsk`; окно подключит WEB-3820).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '@/components/kit/ds';
import { useAppSelector } from '@/hooks/hooks';
import { useOwnMoney } from '@/hooks/useProjectMoneyProfile';
import { useDraftEnabled } from '@/components/kit/useDraftEnabled';
import { useOrgTopUp } from '@/hooks/useOrgTopUp';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import {
  orgShellMenu,
  projectShellMenu,
  shellFlat,
  shellLabel,
} from './shellMenu';
import {
  PALETTE_COMMANDS,
  PALETTE_SECTION_TITLE,
  visibleCommands,
} from './paletteCommands';
import '../../styles/cabinet-shell.css';

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  renderAsk?: (
    q: string,
    back: () => void,
    close: () => void,
  ) => React.ReactNode;
}

interface PalItem {
  label: string;
  icon: IconName;
  path?: string;
  /** Переход на «Баланс» — дверью пополнения `useOrgTopUp`, не по адресу. */
  balance?: boolean;
  kw: string;
  /** Подпись раздела справа (только у команд, `where` эталона). */
  where?: string;
  ai?: boolean;
}

/** Закрыта — ничего не рисует и не держит состояния: открытие начинает с чистого запроса. */
export function CommandPalette({
  open,
  onClose,
  renderAsk,
}: CommandPaletteProps) {
  return open ? <PaletteBody onClose={onClose} renderAsk={renderAsk} /> : null;
}

function PaletteBody({
  onClose,
  renderAsk,
}: Omit<CommandPaletteProps, 'open'>) {
  const s = useStrings(commonStrings);
  const navigate = useNavigate();
  const topUp = useOrgTopUp();
  const user = useAppSelector((state) => state.auth.user);
  const root = useCabinetRootAttributes(user?.theme);
  const ownMoney = useOwnMoney();
  const draft = useDraftEnabled();
  const guest = useAppSelector(
    ({ organizations: o }) =>
      o.orgs.find((x) => x.id === o.activeOrgId)?.role === 'member',
  );
  const [q, setQ] = useState('');
  const [act, setAct] = useState(0);
  const [ask, setAsk] = useState<string | null>(null);

  const menus = {
    project: projectShellMenu({ ownMoney, draft }),
    org: orgShellMenu({ guest }),
  };
  const nav: PalItem[] = [
    ...shellFlat(menus.project).map((i) => ({
      label: shellLabel(i, s),
      icon: i.icon,
      path: i.path,
      kw: i.key,
    })),
    ...shellFlat(menus.org).map((i) => ({
      label: `${shellLabel(i, s)}, ${s.wholeOrganization}`,
      icon: i.icon,
      path: i.path,
      kw: i.key,
    })),
  ];
  const cmds: PalItem[] = visibleCommands(PALETTE_COMMANDS, menus).map((c) => {
    const where = PALETTE_SECTION_TITLE[c.section];
    return {
      label: s[c.label],
      icon: c.icon,
      path: c.path,
      balance: c.balance,
      kw: c.keywords,
      where: where ? s[where] : undefined,
    };
  });
  const ql = q.trim().toLowerCase();
  const hit = (x: PalItem) =>
    !ql || `${x.label} ${x.kw}`.toLowerCase().includes(ql);
  const a = nav.filter(hit).slice(0, ql ? 6 : 5);
  const b = cmds.filter(hit).slice(0, ql ? 8 : 4);
  const items = [...a, ...b];
  if (ql && renderAsk)
    items.push({ label: s.palAsk(q.trim()), icon: 'ai', kw: '', ai: true });

  const run = (it: PalItem | undefined) => {
    if (!it) return;
    if (it.ai) {
      setAsk(q.trim());
      return;
    }
    onClose();
    if (it.balance) void topUp.goToBalance();
    else if (it.path) navigate(it.path);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAct((x) => Math.min(items.length - 1, x + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAct((x) => Math.max(0, x - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(items[act]);
    }
  };
  const row = (it: PalItem, i: number) => (
    <li
      key={i}
      className={i === act ? 'it act' : 'it'}
      onMouseEnter={() => setAct(i)}
      onClick={() => run(it)}
      data-testid="pal-item"
    >
      <Icon name={it.icon} size={16} />
      <span>{it.label}</span>
      {it.where && <span className="w">{it.where}</span>}
    </li>
  );

  return (
    <div {...root} className={`${root.className} pal-host`}>
      <div
        className="pal"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        data-testid="palette"
      >
        <div className="pal-box" role="dialog" aria-label={s.palFind}>
          <div className="pal-in">
            <Icon name="search" size={18} />
            <input
              autoFocus
              value={q}
              placeholder={s.palPlaceholder}
              onChange={(e) => {
                setQ(e.target.value);
                setAct(0);
                setAsk(null);
              }}
              onKeyDown={onKey}
              aria-label={s.palFind}
              data-testid="pal-input"
            />
            <span className="kbd">{s.palEscKey}</span>
          </div>
          {ask !== null && renderAsk ? (
            renderAsk(ask, () => setAsk(null), onClose)
          ) : (
            <ul className="pal-list">
              {a.length > 0 && <li className="pal-grp">{s.palSections}</li>}
              {a.map((it, i) => row(it, i))}
              {b.length > 0 && <li className="pal-grp">{s.palActions}</li>}
              {b.map((it, i) => row(it, a.length + i))}
              {items.length > a.length + b.length &&
                row(items[items.length - 1], items.length - 1)}
            </ul>
          )}
          <div className="pal-foot">
            <span>{s.palHintNav}</span>
            <span>{s.palHintOpen}</span>
            <span>{s.palHintClose}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
